import * as React from 'react';

import { Box } from '@lightspeed/flame/Core';
import { Spinner } from '@lightspeed/flame/Spinner';

import { AlertMessageDTO } from 'root/apps/components/AlertMessageDisplay/AlertMessageDisplay';
import {
  LOG_SOURCE,
  NuorderOrderingActionTypes,
  useNuorderOrdering,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Context';
import { useUpdateItemVendorNum } from 'root/apps/PurchaseOrder/NuorderOrdering/context/Queries';
import { ItemReadiness, RowDataSortable } from 'root/apps/PurchaseOrder/NuorderOrdering/types';
import { computeAlertMessage } from 'root/apps/PurchaseOrder/NuorderOrdering/utils/dataUtils';
import { logError } from 'root/datadog/datadogLogs';

interface RowNuorderCellProps {
  data: RowDataSortable;
  setRowAlertMessage?: (message: AlertMessageDTO | null) => void;
}

export const RowNuorderCell: React.FC<RowNuorderCellProps> = ({ data, setRowAlertMessage }) => {
  const {
    state: {
      isFetchingProducts,
      isFetchingAvailability,
      nuorderProducts,
      nuorderProductAvailability,
    },
    dispatch,
  } = useNuorderOrdering();

  const mutationUpdateItemVendorNum = useUpdateItemVendorNum(data.itemID, data.vendorID);

  const { availability, itemReadiness } = data;

  const showSpinnerImmediate = React.useMemo(() => {
    if (mutationUpdateItemVendorNum.isLoading) return true;
    if (itemReadiness === ItemReadiness.NO_UUID && isFetchingProducts) return true;
    if (itemReadiness === ItemReadiness.NOT_ON_NUORDER && isFetchingAvailability) return true;
    if (itemReadiness === ItemReadiness.NO_AVAILABILITY && isFetchingAvailability) return true;
    if (itemReadiness === ItemReadiness.NOT_READY && isFetchingAvailability) return true;
    return false;
  }, [
    mutationUpdateItemVendorNum.isLoading,
    itemReadiness,
    isFetchingProducts,
    isFetchingAvailability,
  ]);

  const [showSpinner, setShowSpinner] = React.useState(showSpinnerImmediate);

  // Debouncing the spinner visibility to avoid flickering
  React.useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (showSpinnerImmediate) {
      setShowSpinner(true);
    } else {
      timeout = setTimeout(() => setShowSpinner(false), 100);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [showSpinnerImmediate]);

  const alertMessage = React.useMemo<AlertMessageDTO | null>(() => {
    return computeAlertMessage(itemReadiness);
  }, [itemReadiness]);

  React.useEffect(() => {
    if (setRowAlertMessage && !showSpinner) {
      setRowAlertMessage(alertMessage);
    }
  }, [showSpinner, alertMessage, setRowAlertMessage]);

  /**
   * If the item does not have a B2B catalog UUID but its UPC corresponds to a Nuorder product,
   * we update the item vendor number with the Nuorder product's ID and supplier ID.
   */
  React.useEffect(() => {
    if (!nuorderProducts || isFetchingProducts) return;
    if (!data.b2bCatalogUUID && data.upc) {
      const nuorderProduct = nuorderProducts[data.upc];
      if (nuorderProduct && !mutationUpdateItemVendorNum.isLoading) {
        mutationUpdateItemVendorNum.mutate({
          value: data.itemVendorNum || nuorderProduct.supplier_id || 'n/a',
          b2bCatalogUUID: nuorderProduct.id,
        });
      }
    }
  }, [
    data.b2bCatalogUUID,
    data.itemVendorNum,
    data.upc,
    isFetchingProducts,
    mutationUpdateItemVendorNum,
    nuorderProducts,
  ]);

  /**
   * Self-healing logic:
   * If the availability is not found but the item's UPC corresponds
   * to a Nuorder product with a different B2B catalog UUID,
   * we overwrite the item vendor number with the Nuorder product's ID and supplier ID.
   */
  React.useEffect(() => {
    if (
      !nuorderProducts ||
      !nuorderProductAvailability ||
      isFetchingProducts ||
      isFetchingAvailability
    )
      return;
    if (data.b2bCatalogUUID && !nuorderProductAvailability[data.b2bCatalogUUID]) {
      const nuorderProduct = nuorderProducts[data.upc];
      if (
        nuorderProduct &&
        nuorderProduct.id !== data.b2bCatalogUUID &&
        !mutationUpdateItemVendorNum.isLoading
      ) {
        mutationUpdateItemVendorNum.mutate({
          value: data.itemVendorNum || nuorderProduct.supplier_id || 'n/a',
          b2bCatalogUUID: nuorderProduct.id,
        });
      }
    }
  }, [
    availability,
    data.b2bCatalogUUID,
    data.itemVendorNum,
    data.upc,
    isFetchingAvailability,
    isFetchingProducts,
    mutationUpdateItemVendorNum,
    nuorderProductAvailability,
    nuorderProducts,
  ]);

  /**
   * Handle errors in the mutation
   */
  React.useEffect(() => {
    if (mutationUpdateItemVendorNum.error) {
      dispatch({
        type: NuorderOrderingActionTypes.SET_ALERT_MESSAGE,
        payload: {
          type: 'danger',
          title: 'Error matching item UPC on NuORDER',
          message: (
            <>
              An error occurred while matching an item UPC on NuORDER. Please try again later.
              <br />
              If the problem persists, try updating the UPC or removing the vendor ID.
            </>
          ),
        },
      });
      logError(mutationUpdateItemVendorNum.error, {
        source: LOG_SOURCE,
        file: 'RowNuorderCell.tsx',
      });
    }
  }, [dispatch, mutationUpdateItemVendorNum.error]);

  return (
    <Box data-test="rowNuorderCell">
      {showSpinner ? <Spinner data-test="spinner" /> : availability}
    </Box>
  );
};
